import React, { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { Button, Form, Input, Radio, Select, Typography } from "antd";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { mapCountry } from "../Common";
import './paymentMethodSelector.css'

interface PaymentFormData {
  paymentMethod: "credit" | "paypal";
  firstName: string;
  lastName: string;
  cardNumber: string;
  expirationDate: string;
  cvc: string;
  country: string;
  zipCode: string;
}

const paymentLogos = {
  americanExpress: "/images/American_Express_logo_(2018).svg.svg",
  discover: "/images/Discover-logo.svg",
  mastercard: "/images/Mastercard-logo.svg.svg",
  visa: "/images/Visa_2021.svg.svg",
  paypal: "/images/PayPal.svg.png",
};

interface PaymentMethodSelectorProps {
  onFormDataChange?: (data: PaymentFormData) => void;
  initialData?: Partial<PaymentFormData>;
  onValidate?: (isValid: boolean, formData: PaymentFormData) => void;
}

export interface PaymentMethodSelectorRef {
  validateForm: () => Promise<{ isValid: boolean; data?: PaymentFormData; errors?: any }>;
}

export default forwardRef<PaymentMethodSelectorRef, PaymentMethodSelectorProps>(function PaymentMethodSelector({
  onFormDataChange,
  initialData,
  onValidate,
}, ref) {
  const [form] = Form.useForm();
  const [paymentMethod, setPaymentMethod] = useState<"credit" | "paypal">(
    "credit"
  );
  const [isExpanded, setIsExpanded] = useState(true);
  const [formData, setFormData] = useState<PaymentFormData>({
    paymentMethod: initialData?.paymentMethod || "credit",
    firstName: initialData?.firstName || "",
    lastName: initialData?.lastName || "",
    cardNumber: initialData?.cardNumber || "",
    expirationDate: initialData?.expirationDate || "",
    cvc: initialData?.cvc || "",
    country: initialData?.country || "United States",
    zipCode: initialData?.zipCode || "",
  });

  // 暴露验证方法给父组件
  const validateForm = () => {
    return form.validateFields()
      .then((validatedValues) => {
        const newFormData = { ...formData, ...validatedValues };
        setFormData(newFormData);
        onFormDataChange?.(newFormData);
        onValidate?.(true, newFormData);
        return { isValid: true, data: newFormData };
      })
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo);
        onValidate?.(false, formData);
        return { isValid: false, errors: errorInfo };
      });
  };

  // 将验证方法暴露给父组件
  useImperativeHandle(ref, () => ({
    validateForm
  }), []);

  // 调试信息
  useEffect(() => {
    console.log("PaymentMethodSelector mounted, ref:", ref);
    console.log("validateForm function:", validateForm);
  }, [ref, validateForm]);

  const handlePaymentMethodChange = (value: string) => {
    const method = value as "credit" | "paypal";
    setPaymentMethod(method);
    const newFormData = { ...formData, paymentMethod: method };
    setFormData(newFormData);
    onFormDataChange?.(newFormData);
  };

  const handleSubmit = (values: any) => {
    // 手动触发表单验证
    form.validateFields()
      .then((validatedValues) => {
        console.log("Payment form values:", validatedValues);
        onFormDataChange?.(validatedValues);
      })
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo);
        // 验证失败时，错误提示会自动显示
      });
  };

  const CardLogos = () => (
    <div
      className="flex items-center gap-3"
      style={{ display: "flex", alignItems: "center", gap: 12 }}
    >
      <img
        src={paymentLogos.americanExpress}
        alt="American Express"
        style={{ width: 14, height: 14 }}
      />
      <img
        src={paymentLogos.discover}
        alt="Discover"
        style={{ width: 63, height: 14 }}
      />
      <img
        src={paymentLogos.mastercard}
        alt="Mastercard"
        style={{ width: 18, height: 14 }}
      />
      <img
        src={paymentLogos.visa}
        alt="Visa"
        style={{ width: 43, height: 14 }}
      />
      {/* <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          border: "none",
          background: "none",
          cursor: "pointer",
          padding: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          style={{
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          <polyline points="6,9 12,15 18,9"></polyline>
        </svg>
      </button> */}
    </div>
  );

  const VerifiedIcon = () => (
    <div
      style={{
        width: 24,
        height: 24,
        position: "relative",
        display: "inline-block",
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M3 4.25V12.1877C3 15.5966 4.92602 18.713 7.97508 20.2375L12 22.25L16.0249 20.2375C19.074 18.713 21 15.5966 21 12.1877V4.25L20.2159 4.33712C17.461 4.64322 14.6901 3.92152 12.4346 2.31043L12 2L11.5654 2.31043C9.30988 3.92152 6.53898 4.64322 3.78411 4.33712L3 4.25Z"
          fill="#474849"
        />
        <path
          d="M10.875 14.375L8.625 12.125M3 4.25V12.1877C3 15.5966 4.92602 18.713 7.97508 20.2375L12 22.25L16.0249 20.2375C19.074 18.713 21 15.5966 21 12.1877V4.25L20.2159 4.33712C17.461 4.64322 14.6901 3.92152 12.4346 2.31043L12 2L11.5654 2.31043C9.30988 3.92152 6.53898 4.64322 3.78411 4.33712L3 4.25Z"
          stroke="#474849"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <rect
          x="6"
          y="11.5354"
          width="2"
          height="7"
          transform="rotate(-45 6 11.5354)"
          fill="white"
        />
        <rect
          x="16.6055"
          y="8"
          width="2"
          height="10"
          transform="rotate(45 16.6055 8)"
          fill="white"
        />
      </svg>
    </div>
  );

  return (
    <div
      style={{
        backgroundColor: "#f6f6f6",
        borderRadius: 12,
        padding: 16,
        width: "100%",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div
        style={{
          margin: 0,
          marginBottom: 12,
          color: "#000",
          fontFamily: "Inter",
          fontSize: 16,
          fontStyle: "normal",
          fontWeight: 600,
          letterSpacing: 0.5,
        }}
      >
        Select your payment method
      </div>

      <Form
        form={form}
        onFinish={handleSubmit}
        initialValues={formData}
        layout="vertical"
        validateTrigger={[]}
      >
        {/* Secure Checkout Header */}
        <div
          style={{
            backgroundColor: "#f5f5f7",
            border: "1px solid #d9d9d9",
            borderRadius: "10px 10px 0 0",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: -1,
          }}
        >
          <VerifiedIcon />
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#222222",
            }}
          >
            Secure Checkout
          </span>
        </div>

        {/* Credit Card Row */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #d9d9d9",
            borderTop: "1px solid #d9d9d9",
            padding: "12px 16px",
            paddingTop: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: -1,
            borderRadius: !isExpanded ? "0 0 12px 12px": '0',
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                fontSize: 16,
                fontWeight: 500,
                color: "#222222",
              }}
            >
              Credit Card
            </span>
          </div>
          
          {/* Payment Method Icons */}
          <div
            className="flex items-center gap-3"
            style={{ display: "flex", alignItems: "center", gap: 12 }}
          >
            <img
              src={paymentLogos.americanExpress}
              alt="American Express"
              style={{ width: 14, height: 14 }}
            />
            <img
              src={paymentLogos.discover}
              alt="Discover"
              style={{ width: 63, height: 14 }}
            />
            <img
              src={paymentLogos.mastercard}
              alt="Mastercard"
              style={{ width: 18, height: 14 }}
            />
            <img
              src={paymentLogos.visa}
              alt="Visa"
              style={{ width: 43, height: 14 }}
            />
            
            {/* Expand/Collapse Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                padding: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              >
                <polyline points="6,9 12,15 18,9"></polyline>
              </svg>
            </button>
          </div>
        </div>

        {/* Payment Methods Container */}
        <div
          className='price-select-box'
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #d9d9d9",
            borderTop: "none",
            borderRadius: "0 0 10px 10px",
            marginBottom: -1,
            display: isExpanded ? 'block' : 'none',
            transition: 'all 0.3s ease'
          }}
        >
          {/* Credit Card Option */}
          <div
            style={{
              padding: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: isExpanded ? 20 : 0,
              }}
            >
              {/* <div style={{ fontSize: 16, fontWeight: 500 }}>Credit card</div>
              <CardLogos /> */}
            </div>

            {/* Credit Card Form */}
            {isExpanded && (
              <div style={{ marginTop: 20 }} className="payment-form-content">
                {/* Name Fields */}
                <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name="firstName"
                      label="First name"
                      style={{ marginBottom: 6 }}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      rules={[
                        {
                          required: true,
                          message: "Please input your first name",
                        },
                      ]}
                      validateTrigger={[]}
                    >
                      <Input
                        placeholder="First name on card"
                        style={{
                          background: "#FCFCFC",
                          border: "0.875px solid #ECECEC",
                          borderRadius: 7,
                          padding: "13px 12px",
                          fontSize: 16,
                          color: "#222",
                          height: 46,
                        }}
                      />
                    </Form.Item>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name="lastName"
                      label="Last name"
                      style={{ marginBottom: 6 }}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      rules={[
                        {
                          required: true,
                          message: "Please input your last name",
                        },
                      ]}
                      validateTrigger={[]}
                    > 
                      <Input
                        placeholder="Last name on card"
                        style={{
                          background: "#FCFCFC",
                          border: "0.875px solid #ECECEC",
                          borderRadius: 7,
                          padding: "13px 12px",
                          fontSize: 16,
                          color: "#222",
                          height: 46,
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* Card Number */}
                <div style={{ marginBottom: 20 }}>
                  <Form.Item
                    name="cardNumber"
                    label="Credit or debit card number"
                    style={{ marginBottom: 6 }}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                    rules={[
                      {
                        required: true,
                        message: "Please input your card number",
                      },
                    ]}
                    validateTrigger={[]}
                  >
                    <CardNumberElement
                      options={{
                        style: {
                          base: {
                            fontSize: "16px",
                            color: "#222",
                            backgroundColor: "#FCFCFC",
                            "::placeholder": {
                              color: "#808080",
                              fontWeight: 400,
                              // fontFamily: "Inter",
                            },
                          },
                        },
                      }}
                    />
                  </Form.Item>
                </div>

                {/* Expiry and CVC */}
                <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name="expirationDate"
                      label="Expiration date"
                      style={{ marginBottom: 6 }}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      rules={[
                        {
                          required: true,
                          message: "Please input your expiration date",
                        },
                      ]}
                      validateTrigger={[]}
                    >
                      <CardExpiryElement
                        options={{
                          style: {
                            base: {
                              fontSize: "16px",
                              color: "#222",
                              backgroundColor: "#FCFCFC",
                              "::placeholder": {
                                color: "#808080",
                                fontWeight: 400,
                                // fontFamily: "Inter",
                              },
                            },
                          },
                        }}
                      />
                    </Form.Item>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name="cvc"
                      label="CVC"
                      style={{ marginBottom: 6 }}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      rules={[
                        {
                          required: true,
                          message: "Please input your CVC",
                        },
                      ]}
                      validateTrigger={[]}
                    >
                      <CardCvcElement
                        options={{
                          style: {
                            base: {
                              fontSize: "16px",
                              color: "#222",
                              backgroundColor: "#FCFCFC",
                              "::placeholder": {
                                color: "#808080",
                                fontWeight: 400,
                                // fontFamily: "Inter",
                              },
                            },
                          },
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* Country and ZIP */}
                <div style={{ display: "flex", gap: 20 }}>
                  <div style={{ flex: 1 }} className='price-select-item'>
                    <Form.Item
                      name="country"
                      label="Country"
                      style={{ marginBottom: 6 }}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                    >
                      <Select
                        placeholder="Select country"
                        style={{
                          background: "#FCFCFC",
                          fontSize: 16,
                          color: "#222",
                          height: 46,
                        }}
                        options={Object.entries(mapCountry)
                          .map(([value, label]) => ({
                            label:
                              label.charAt(0).toUpperCase() + label.slice(1),
                            value,
                          }))
                          .sort((a, b) => a.label.localeCompare(b.label))}
                      />
                    </Form.Item>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name="zipCode"
                      label="ZIP / Postal code"
                      style={{ marginBottom: 6 }}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      rules={[
                        {
                          required: true,
                          message: "Please input your ZIP / Postal code",
                        },
                      ]}
                      validateTrigger={[]}
                    >
                      <Input
                        placeholder="12345"
                        style={{
                          background: "#FCFCFC",
                          border: "0.875px solid #ECECEC",
                          borderRadius: 7,
                          padding: "13px 12px",
                          fontSize: 16,
                          color: "#222",
                          height: 46,
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Form>
    </div>
  );
});
